import { useContext, useState, useRef, useEffect, useCallback } from "react";
import { Link, useInRouterContext, useNavigate, useLocation } from "react-router-dom";
import { LocaleContext } from "../../state/LocaleContext.js";
import LocaleSelector from "./LocaleSelector.jsx";
import LoginModal from "../nav/LoginModal.jsx";
import { Badge } from "@octane/design-system/widgets";

export default function MainNav({
  groups,
  pages,
  statuses,
  group,
  page,
  role,
  setRole,
  showLogin,
  setShowLogin,
  isReady,
  settings,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // Test mode toggle (persisted in sessionStorage)
  const [testModeActive, setTestModeActive] = useState(() => sessionStorage.getItem('avatarTestMode') === '1');

  // Dev overlay toggle (persisted in localStorage, no reload needed)
  const [devOverlaysActive, setDevOverlaysActive] = useState(() => localStorage.getItem('devOverlays') === '1');

  // Decode username from JWT for display (no verification needed — display only)
  const displayUsername = (() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || payload.sub || null;
    } catch { return null; }
  })();

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setSearchResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
        if (!res.ok) return;
        setSearchResults(await res.json());
      } catch (_) {}
    }, 300);
  }, []);

  // Close search on click outside
  useEffect(() => {
    if (!searchOpen) return;
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
        setSearchQuery("");
        setSearchResults(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchOpen]);

  // locale specific labels
  const { tUI, t } = useContext(LocaleContext);
  const labelGroups = tUI("label_groups", "Groups");
  const labelBackToMap = tUI("label_back_to_map", "Back to Map");
  const labelSignIn = tUI("label_sign_in", "Sign in");
  const labelSignOut = tUI("label_sign_out", "Sign out");
  const labelCms = tUI("label_cms", "CMS");
  const labelAnalytics = tUI("label_analytics", "Analytics");
  const labelAdmin = tUI("label_admin", "Admin");
  const labelTalkToExpert = tUI("label_talk_to_expert", "Talk to an Expert");
  const labelComment = tUI("label_leave_feedback", "Leave Feedback");
  const settingsLogoUrl = t("settings", 1, "logo_url", "https://media.cgprojecttracker.com/api/media/images/shared/brand/project-tracker-logo-sml.png");
  const settingsLogoAlt = t("settings", 1, "logo_alt", "Project Tracker");
  const cityName = settings?.city?.name || "Casa Grande";
  const settingsCityLogoUrl = t("settings", 1, "city_logo_url", "https://media.cgprojecttracker.com/api/media/images/shared/brand/logo-cityofcasagrande.png");
  const settingsCityLogoAlt = t("settings", 1, "city_logo_alt", "City of Casa Grande: Stronger United");

  // If App is ever rendered without a Router, fall back to <a>
  function BrandLink({ children }) {
    const inRouter = useInRouterContext();
    const className = "navbar-brand ms-2 ms-md-3 me-0";
    return inRouter ? (
      <Link to="/" className={className}>
        {children}
      </Link>
    ) : (
      <a href="/" className={className}>
        {children}
      </a>
    );
  }

  return (
    <nav className="navbar fixed-top navbar-expand-md bg-white py-2 py-md-2">
      <div className="container-fluid px-0 pt-0">
        {/* Flex column on md+ */}
        <div className="d-flex flex-column flex-md-row align-items-md-center w-100">
          {/* Brand at top/left */}
          <div className="flex-shrink-0 d-flex align-items-center w-100 octane-w-md-auto">
            <BrandLink>
              <span style={{ position: "relative", display: "inline-block", textDecoration: "none" }}>
                <img
                  id="header-logo"
                  src={settingsLogoUrl}
                  alt={settingsLogoAlt}
                  className="img-fluid"
                />
                <span className="brand-city-name" style={{
                  position: "absolute",
                  bottom: "calc(100% - 15px)",
                  left: "14.6%",
                  fontSize: "clamp(0.35rem, 2.4vw, 0.7em)",
                  fontWeight: 500,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase",
                  letterSpacing: "0.2em",
                  whiteSpace: "nowrap",
                }}>
                  {cityName}
                </span>
              </span>
            </BrandLink>
            <div className="d-flex align-items-center ms-auto">
              <img
                id="header-logo-2-small"
                src={settingsCityLogoUrl}
                alt={settingsCityLogoAlt}
                className="d-none"
              />
              <button
                id="octane-hamburger"
                className="navbar-toggler mt-1 mx-2 d-md-none"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#mainNavbar"
                aria-controls="mainNavbar"
                aria-expanded="false"
                aria-label="Toggle navigation"
                aria-haspopup="true"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </div>
          </div>
          {/* Navbar collapses below brand on md+ */}
          <div className="flex-grow-1">
            <div className="navbar-collapse mt-2 collapse" id="mainNavbar">
              <ul className="navbar-nav ms-auto align-items-md-center">
                <li className="nav-item mx-3 d-md-none">
                  <button
                    id="nav-avatar"
                    type="button"
                    className="nav-link btn btn-link"
                    title={labelTalkToExpert}
                    aria-label={labelTalkToExpert}
                    onClick={() => {
                      document.dispatchEvent(
                        new CustomEvent("openAvatarPanel")
                      );
                    }}
                  >
                    {labelTalkToExpert}
                  </button>
                </li>
                <li className="nav-item mx-3 d-md-none">
                  <button
                    id="nav-comment"
                    type="button"
                    className="nav-link btn btn-link"
                    title={labelComment}
                    aria-label={labelComment}
                  >
                    {labelComment}
                  </button>
                </li>

                {/* Search — always-visible input on xl+, icon toggle on md-lg, full-width in mobile collapse */}
                <li className="nav-item mx-3 mx-md-2 flex-xl-grow-1 d-flex justify-content-xl-center" ref={searchRef} style={{ position: "relative" }}>
                  {/* Desktop xl+: always-visible input */}
                  <div className="d-none d-xl-flex align-items-center" style={{ position: "relative", width: "100%", maxWidth: "320px" }}>
                    <i className="bi bi-search" style={{ position: "absolute", left: "10px", color: "#999", fontSize: "0.8rem", pointerEvents: "none" }}></i>
                    <input
                      type="text"
                      className="form-control form-control-sm nav-search-input"
                      placeholder={tUI("label_search", "Search") + "..."}
                      aria-label={tUI("label_search", "Search")}
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); doSearch(e.target.value); }}
                      onKeyDown={(e) => { if (e.key === "Escape") { setSearchQuery(""); setSearchResults(null); e.target.blur(); } }}
                      style={{ paddingLeft: "30px", fontSize: "0.85rem", border: "1px solid #dee2e6", borderRadius: "4px" }}
                    />
                  </div>
                  {/* md-lg: icon toggle */}
                  <div className="d-none d-md-flex d-xl-none align-items-center">
                    {!searchOpen ? (
                      <a
                        className="nav-link py-0"
                        href="#"
                        title={tUI("label_search", "Search")}
                        aria-label={tUI("label_search", "Search")}
                        onClick={(e) => { e.preventDefault(); setSearchOpen(true); }}
                      >
                        <i className="bi bi-search" title={tUI("label_search", "Search")}></i>
                      </a>
                    ) : (
                      <div className="d-flex align-items-center" style={{ position: "relative" }}>
                        <i className="bi bi-search" style={{ position: "absolute", left: "10px", color: "#999", fontSize: "0.8rem", pointerEvents: "none" }}></i>
                        <input
                          autoFocus
                          type="text"
                          className="form-control form-control-sm nav-search-input"
                          placeholder={tUI("label_search", "Search") + "..."}
                          aria-label={tUI("label_search", "Search")}
                          value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); doSearch(e.target.value); }}
                          onKeyDown={(e) => { if (e.key === "Escape") { setSearchOpen(false); setSearchQuery(""); setSearchResults(null); } }}
                          style={{ width: "200px", paddingLeft: "30px", fontSize: "0.85rem", border: "1px solid #dee2e6", borderRadius: "4px" }}
                        />
                      </div>
                    )}
                  </div>
                  {/* Mobile: full-width input in collapse */}
                  <div className="d-md-none w-100">
                    <input
                      type="text"
                      className="form-control form-control-sm nav-search-input"
                      placeholder={tUI("label_search", "Search") + "..."}
                      aria-label={tUI("label_search", "Search")}
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); doSearch(e.target.value); }}
                      onKeyDown={(e) => { if (e.key === "Escape") { setSearchQuery(""); setSearchResults(null); } }}
                      style={{ fontSize: "0.85rem", border: "1px solid #dee2e6", borderRadius: "4px" }}
                    />
                  </div>
                  {searchResults && (searchResults.projects.length > 0 || searchResults.pages.length > 0) && (
                    <div className="search-results-dropdown" style={{
                      position: "absolute", top: "100%", right: 0, zIndex: 1050,
                      backgroundColor: "#fff", border: "1px solid #dee2e6", borderRadius: "4px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxHeight: "400px", overflowY: "auto",
                      minWidth: "300px", marginTop: "4px",
                    }}>
                      {searchResults.projects.length > 0 && (
                        <>
                          <div style={{ padding: "6px 12px", fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", color: "#6c757d", borderBottom: "1px solid #eee" }}>
                            {tUI("label_projects", "Projects")}
                          </div>
                          {searchResults.projects.map((p) => (
                            <a
                              key={`p-${p.id}`}
                              href={`/detail?project=${p.id}`}
                              className="d-block text-decoration-none"
                              style={{ padding: "8px 12px", borderBottom: "1px solid #f5f5f5", color: "#212529" }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                              onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }}
                            >
                              <div style={{ fontWeight: 500, fontSize: "0.85rem" }}>{p.title}</div>
                              <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                                {p.status_name && <Badge label={p.status_name} className="me-1" uppercase={false} background={p.status_color || "#6c757d"} color="#fff" fontSize="0.65rem" borderRadius="0.375rem" padding="0.35em 0.65em" />}
                                {p.project_type_name && <span>{p.project_type_name}</span>}
                                {p.project_code && <span className="ms-1">({p.project_code})</span>}
                              </div>
                            </a>
                          ))}
                        </>
                      )}
                      {searchResults.pages.length > 0 && (
                        <>
                          <div style={{ padding: "6px 12px", fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", color: "#6c757d", borderBottom: "1px solid #eee" }}>
                            {tUI("label_pages", "Pages")}
                          </div>
                          {searchResults.pages.map((pg) => (
                            <a
                              key={`pg-${pg.id}`}
                              href={pg.group_slug && pg.slug ? `/${pg.group_slug}/home/${pg.slug}` : "#"}
                              className="d-block text-decoration-none"
                              style={{ padding: "8px 12px", borderBottom: "1px solid #f5f5f5", color: "#212529" }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                              onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults(null); }}
                            >
                              <div style={{ fontWeight: 500, fontSize: "0.85rem" }}>{pg.title}</div>
                              {pg.subtitle && <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>{pg.subtitle}</div>}
                              {pg.group_title && <div style={{ fontSize: "0.7rem", color: "#999" }}>{pg.group_title}</div>}
                            </a>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </li>

                <li className="nav-item mx-3 mx-md-2 dropdown">
                  <a
                    className="nav-link dropdown-toggle"
                    href="#"
                    id="navbarDropdownBackToMapMenuLink"
                    role="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <span className="d-inline d-md-none d-xl-inline">
                      {labelBackToMap}
                    </span>{" "}
                    <i className="bi bi-geo-alt d-none d-md-inline d-xl-none" title={labelBackToMap}></i>
                  </a>
                  <ul
                    className="dropdown-menu"
                    aria-labelledby={labelBackToMap}
                  >
                    {(statuses?.rows || []).map((s) => (
                      <li key={s.id}>
                        <a
                          className="dropdown-item"
                          href={`/?status=${s.id}`}
                        >
                          {t("status", s.id, "name", s.name)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>

                <LocaleSelector />

                {/* Admin dropdown with nested Dev submenu - visible to editor/admin */}
                {(role === "editor" || role === "admin") && (
                  <li className="nav-item mx-3 mx-md-2 dropdown">
                    <a
                      className="nav-link dropdown-toggle"
                      href="#"
                      id="navbarDropdownAdminMenuLink"
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span className="d-inline d-md-none d-xl-inline">
                        {labelAdmin}
                      </span>{" "}
                      <i className="bi bi-gear d-none d-md-inline d-xl-none" title={labelAdmin}></i>
                    </a>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby="navbarDropdownAdminMenuLink"
                    >
                      <li>
                        {(() => {
                          // Build CMS host based on current location.hostname.
                          const buildCmsUrl = () => {
                            try {
                              const host = window.location.hostname || "";
                              if (host.startsWith("project-tracker.")) {
                                return `${window.location.protocol}//${host.replace("project-tracker.", "cms.")}`;
                              }
                              const parts = host.split(".");
                              if (parts.length >= 3) {
                                const base = parts.slice(1).join(".");
                                return `${window.location.protocol}//cms.${base}`;
                              }
                              return `${window.location.protocol}//cms.${host}`;
                            } catch (e) {
                              return "#";
                            }
                          };

                          const cmsUrl = buildCmsUrl();

                          return (
                            <a
                              className="dropdown-item"
                              href={cmsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={labelCms}
                              aria-label={labelCms}
                            >
                              {labelCms}
                            </a>
                          );
                        })()}
                      </li>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        {(() => {
                          // Build analytics host based on current location.hostname.
                          const buildAnalyticsUrl = () => {
                            try {
                              const host = window.location.hostname || "";
                              if (host.startsWith("project-tracker.")) {
                                return `${window.location.protocol}//${host.replace("project-tracker.", "chat-analytics.")}`;
                              }
                              const parts = host.split(".");
                              if (parts.length >= 3) {
                                const base = parts.slice(1).join(".");
                                return `${window.location.protocol}//chat-analytics.${base}`;
                              }
                              return `${window.location.protocol}//chat-analytics.${host}`;
                            } catch (e) {
                              return "#";
                            }
                          };

                          const analyticsUrl = buildAnalyticsUrl();

                          return (
                            <a
                              className="dropdown-item"
                              href={analyticsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={labelAnalytics}
                              aria-label={labelAnalytics}
                            >
                              {labelAnalytics}
                            </a>
                          );
                        })()}
                      </li>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/admin/alerts">
                          {tUI("label_alerts", "Alerts")}
                        </Link>
                      </li>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      {/* Dev expandable section with Groups and SQL */}
                      <li>
                        <a
                          className="dropdown-item dropdown-toggle"
                          href="#"
                          id="navbarDropdownDevMenuLink"
                          role="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#devSubmenu"
                          aria-expanded="false"
                          aria-controls="devSubmenu"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const submenu = document.getElementById('devSubmenu');
                            submenu?.classList.toggle('show');
                          }}
                        >
                          {tUI("label_dev", "Dev")}
                        </a>
                        <div className="collapse" id="devSubmenu">
                          <div className="ps-3">
                            <span className="dropdown-header">{labelGroups}</span>
                            {(groups?.rows || []).map((g) => (
                              <Link key={g.id} className="dropdown-item" to={`/${g.slug}`}>
                                {t("groups", g.id, "title", g.title)}
                              </Link>
                            ))}
                            <hr className="dropdown-divider" />
                            <button
                              type="button"
                              className="dropdown-item d-flex align-items-center gap-2"
                              onClick={() => {
                                if (testModeActive) {
                                  sessionStorage.removeItem('avatarTestMode');
                                } else {
                                  sessionStorage.setItem('avatarTestMode', '1');
                                }
                                setTestModeActive(!testModeActive);
                                window.location.reload();
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: testModeActive ? "#249649" : "#aaa",
                                  flexShrink: 0,
                                }}
                              />
                              {tUI("label_test_mode", "Test Mode")}
                            </button>
                            <button
                              type="button"
                              className="dropdown-item d-flex align-items-center gap-2"
                              onClick={() => {
                                const next = !devOverlaysActive;
                                if (next) {
                                  localStorage.setItem('devOverlays', '1');
                                } else {
                                  localStorage.removeItem('devOverlays');
                                }
                                setDevOverlaysActive(next);
                                window.dispatchEvent(new Event('devOverlaysChange'));
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: devOverlaysActive ? "#1769e8" : "#aaa",
                                  flexShrink: 0,
                                }}
                              />
                              {tUI("label_template_overlays", "Template Overlays")}
                            </button>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </li>
                )}
                {!role && (
                  <li className="nav-item mx-3 mx-md-2">
                    <a className="nav-link" onClick={() => setShowLogin(true)}>
                      <span className="d-inline d-md-none d-xl-inline">
                        {labelSignIn}
                      </span>{" "}
                      <i className="bi bi-box-arrow-in-right d-none d-md-inline d-xl-none" title={labelSignIn}></i>
                    </a>
                    {showLogin && (
                      <LoginModal
                        onLogin={(data) => setRole(data.role)}
                        onClose={() => setShowLogin(false)}
                      />
                    )}
                  </li>
                )}
                {role && (
                  <li className="nav-item mx-3 mx-md-2 d-flex align-items-center gap-2">
                    {displayUsername && (
                      <span className="d-none d-xl-inline" style={{ fontSize: "0.875rem", color: "#5a6a78" }}>
                        {displayUsername}
                      </span>
                    )}
                    <a
                      href="#sign-out"
                      className="nav-link"
                      onClick={(e) => {
                        e.preventDefault();
                        localStorage.removeItem("token");
                        localStorage.removeItem("role");
                        setRole(null);

                        // Redirect to home if on internal page
                        if (location.pathname.startsWith("/internal/")) {
                          navigate("/");
                        }
                      }}
                    >
                      <span className="d-inline d-md-none d-xl-inline">
                        {labelSignOut}
                      </span>{" "}
                      <i className="bi bi-box-arrow-right d-none d-md-inline d-xl-none" title={labelSignOut}></i>
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
          {/* City logo and toggler remain as is */}
          <img
            id="header-logo-2"
            src={settingsCityLogoUrl}
            alt={settingsCityLogoAlt}
            className="img-fluid navbar-brand d-none d-md-block mx-3"
          />
        </div>
      </div>
    </nav>
  );
}
